import { io as IO, Socket } from 'socket.io-client'

/**
 * Realtime Events manager.
 */
export default class RealTimeEvents {
  /**
   * Internal handlers map.
   */
  _handlers = new Map()

  io: Socket | null = null

  /**
   * Connects to the specified host and attempts to authenticate.
   */
  connect(
    host: string,
    token: string,
    globalParams?: { [key: string]: unknown },
  ) {
    this.disconnect()
    const url = new URL(host)
    const pathname = url.pathname.endsWith('/')
      ? url.pathname.slice(0, -1)
      : url.pathname
    const io = (this.io = IO(url.origin, {
      path: `${pathname}/socket.io`,
      transports: ['websocket'],
      query: globalParams,
    }))
    io.on('event', (evt: any) => {
      this.trigger('event', evt)
    })
    io.once('connect', () => {
      io.emit('authenticate', { token }, (result: any) => {
        if (result.success) {
          this.trigger('authenticate:success', result.data)
          return
        }
        console.error('Could not authenticate with event server: ', result)
        this.trigger('authenticate:error', result)
      })
    })
    return this
  }

  /**
   * Disconnects.
   */
  disconnect() {
    if (this.io) {
      this.io.close()
    }
    this.io = null
  }

  /**
   * Subscribes to a real-time event with the specified type.
   * Returns an unsubscribe function.
   */
  subscribe<T extends RealtimeEvent>(
    event: T['type'],
    handler: RealtimeEventHandler<T>,
  ) {
    return this.on('event', (evt: any) => {
      if (evt.type === event) {
        handler(evt)
      }
    })
  }

  /**
   * Subscribes to an event on the class. To subscribe to specific
   * real-time events, use `subscribe`.
   */
  on<T extends RealtimeEvent>(
    event: T['type'],
    handler: RealtimeEventHandler<T['data']>,
  ) {
    let handlers = this._handlers.get(event)
    const _shouldSet = !handlers
    handlers = handlers || []
    if (_shouldSet) {
      this._handlers.set(event, handlers)
    }

    handlers.push(handler)
    const removeListener = () => {
      this._removeHandler(event, handler)
    }

    return removeListener
  }

  /**
   * Removes a specific handler, or an entire class of handlers.
   */
  off<T extends RealtimeEvent>(
    event: T['type'],
    handler: RealtimeEventHandler<T['data']> | undefined,
  ) {
    if (event && handler) {
      this._removeHandler(event, handler)
      return
    }

    if (event && !handler) {
      this._handlers.set(event, [])
      return
    }

    this._handlers = new Map()
  }

  /**
   * Triggers the specified event.
   */
  private trigger<T extends RealtimeEvent>(event: T['type'], data: T) {
    const handlers = this._handlers.get(event)
    if (handlers) {
      handlers.forEach((h: RealtimeEventHandler<T>) => h(data))
    }

    return this
  }

  /**
   * Removes a single handler.
   */
  _removeHandler<T extends RealtimeEvent>(
    event: T['type'],
    handler: RealtimeEventHandler<T['data']>,
  ) {
    const handlers = this._handlers.get(event)
    if (handlers) {
      const idx = handlers.indexOf(handler)
      if (idx > -1) {
        handlers.splice(idx, 1)
      }
    }
  }
}

export interface RealtimeEvent {
  type: string
  data: unknown
}

export type RealtimeEventHandler<T> = (data: T) => unknown
